import { UIPlugin } from '@uppy/core';
import { Provider } from '@uppy/companion-client';
import { ProviderViews } from '@uppy/provider-views';
import { h } from 'preact';
import httpRequest from 'utils/httpRequest';
import { prepareFamilySearchAuthUrl } from 'ecommerce/components/signInWithFamilySearch';
import { ICON_TYPE } from 'components/icon';

import ancestorsApi from '../api/ancestors';

const MIME_TYPE_BY_FEXT = {
  apng: 'image/apng',
  avif: 'image/avif',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  pjpeg: 'image/jpeg',
  pjp: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

export default class FamilySearchProvider extends UIPlugin {
  constructor(uppy, opts) {
    super(uppy, opts);
    this.id = 'familysearch';
    this.title = 'FamilySearch';

    Provider.initPlugin(this, opts);

    this.icon = () =>
      h(
        'svg',
        {
          'aria-hidden': 'true',
          focusable: 'false',
          width: '32',
          height: '32',
          viewBox: "0 0 92 92",
          fill: "currentColor",
          "fill-rule": ICON_TYPE.FAMILY_SEARCH.fillRule,
          className: "text-familysearch"
        },
        ICON_TYPE.FAMILY_SEARCH.svgPath.map(d => h('path', { d })),
      );

    const fetchAncestryImage = async (url, callback) => {
      try {
        const response = await httpRequest(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        return callback(true, blobUrl);
      } catch (e) {
        return callback(false, null);
      }
    };

    this.provider = new Provider(uppy, {
      companionUrl: this.opts.companionUrl,
      provider: 'familysearch',
      pluginId: this.id,
    });

    this.provider.authUrl = () => {
      return prepareFamilySearchAuthUrl('referer');
    };

    this.provider.list = async directory => {
      if (this.opts.ancestryAuth == null) {
        this.setPluginState({ authenticated: false, error: true });
        return {
          items: [],
        };
      }

      const api = ancestorsApi("familysearch", this.opts.paraboAccessToken, this.opts.ancestryAuth);

      if (directory != null) {
        const [treeId, pageId] = directory.split(':::');
        const { data, error } = await api.getTreeMedia(treeId, pageId, {
          pageSize: 15,
        });
        if (error) {
          if (error.code === 401) {
            this.setPluginState({ authenticated: false, error });
          }
          return {
            items: [],
          };
        }

        const items = await Promise.all(
          data.map(medium => {
            return new Promise(res => {
              const mimeType = MIME_TYPE_BY_FEXT[medium.Fext];
              if (uppy.opts.restrictions.allowedFileTypes.indexOf(mimeType) < 0) {
                return res({ valid: false });
              }

              return fetchAncestryImage(medium.ThumbnailUrl, (success, blobUrl) => {
                if (success) {
                  res({
                    isFolder: false,
                    icon: blobUrl,
                    name: [medium.MediumId, medium.Fext].join('.'),
                    mimeType,
                    id: medium.MediumId,
                    size: null,
                    thumbnail: medium.ThumbnailUrl,
                    requestPath: [medium.MediumId, medium.Url].join(':::'),
                  });
                } else {
                  res({
                    valid: false,
                  });
                }
              });
            });
          }),
        );

        return {
          items: items.filter(item => item.valid !== false),
          nextPagePath: [treeId, parseInt(pageId, 10) + 1].join(':::'),
        };
      }

      const { data, error } = await api.getTrees();
      if (error) {
        if (error.code === 401) {
          this.setPluginState({ authenticated: false, error });
        }
        return {
          items: [],
        };
      }

      return {
        items: data.map(tree => ({
          isFolder: true,
          icon: 'folder',
          name: tree.name,
          id: tree.id,
          requestPath: [tree.id, 1].join(':::'),
          isCheckboxDisabled: true,
        })),
      };
    };

    this.provider.fileUrl = requestPath => {
      const [id, url] = requestPath.split(':::');
      return `${this.opts.companionUrl}/${this.provider.provider
        }/get/${id}?url=${encodeURIComponent(url)}`;
    };

    this.onFirstRender = this.onFirstRender.bind(this);
    this.render = this.render.bind(this);
  }

  install() {
    this.view = new ProviderViews(this, {
      provider: this.provider,
    });
    const { target } = this.opts;

    if (target) {
      this.mount(target, this);
    }
  }

  onMount() {
    super.onMount();
  }

  uninstall() {
    this.view.tearDown();
    this.unmount();
  }

  onFirstRender() {
    return Promise.all([
      new Promise(res => {
        httpRequest(`${this.opts.companionUrl}/${this.id}/token`)
          .then(response => {
            return response.json();
          })
          .then(json => {
            this.provider.setAuthToken(json.authToken);
            this.setPluginState({
              authenticated: this.getPluginState().error == null,
            });
            return res();
          })
          .catch(e => {
            this.setPluginState({ error: true });
          });
      }),
      this.view.getFolder(),
    ]);
  }

  render(state) {
    const viewOptions = {};

    if (this.getPluginState().files.length && !this.getPluginState().folders.length) {
      viewOptions.viewType = 'grid';
    }
    viewOptions.showFilter = false;

    return this.view.render(state, viewOptions);
  }
}
FamilySearchProvider.VERSION = '1.0.0';
